import { useState } from "react";
import { Check, Copy, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface CopyIdProps {
  /** Optional uppercase typo-label row rendered above the value. */
  label?: string;
  /** The text to display in the code block and write to the clipboard. */
  value: string;
  /** Layout variant. "default" wraps the value in a bordered card; "compact" is a tight inline row. */
  variant?: "default" | "compact";
  /** Override for the copy button's accessible name. Defaults to "Copy". */
  ariaLabel?: string;
}

type CopyState = "idle" | "copied" | "failed";

const RESET_MS = 2000;

export function CopyId({
  label,
  value,
  variant = "default",
  ariaLabel = "Copy",
}: CopyIdProps) {
  const [state, setState] = useState<CopyState>("idle");

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(value);
      setState("copied");
      setTimeout(() => setState("idle"), RESET_MS);
    } catch {
      setState("failed");
      setTimeout(() => setState("idle"), RESET_MS);
    }
  }

  const buttonLabel =
    state === "copied"
      ? `${ariaLabel} — copied`
      : state === "failed"
        ? "Copy failed"
        : ariaLabel;

  const icon =
    state === "copied" ? (
      <Check className="h-4 w-4 text-[color:var(--color-success)]" />
    ) : state === "failed" ? (
      <X className="h-4 w-4 text-[color:var(--color-danger)]" />
    ) : (
      <Copy className="h-4 w-4" />
    );

  const codeClasses =
    variant === "compact"
      ? "flex-1 truncate rounded bg-[color:var(--color-surface)] px-2 py-1 typo-mono text-xs text-[color:var(--color-text-hi)]"
      : "flex-1 break-all rounded bg-[color:var(--color-surface)] px-3 py-2 typo-mono text-sm text-[color:var(--color-text-hi)]";

  const rowClasses =
    variant === "compact"
      ? "flex items-center gap-2"
      : "flex items-center gap-2 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-elevated)] p-3";

  return (
    <div className="space-y-1">
      {label && (
        <p
          className={cn(
            "typo-label uppercase text-[color:var(--color-text-md)]",
            variant === "compact" ? "mb-0.5" : "mb-1",
          )}
        >
          {label}
        </p>
      )}
      <div className={rowClasses}>
        <code className={codeClasses} title={value}>
          {value}
        </code>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => void handleCopy()}
          aria-label={buttonLabel}
        >
          {icon}
        </Button>
      </div>
    </div>
  );
}
